import {
  TranscribeUploadedFileDocument,
  type TranscribeUploadedFileMutation,
  type TranscribeUploadedFileMutationVariables,
} from "@/generated/graphql";
import { AI_CREDITS_MESSAGE, isAiCreditsExhausted } from "@/lib/utils/aiCredits";
import { Button, IconButton } from "@ai-teach/ui-web";
import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { FiX } from "react-icons/fi";
import { TagsSection } from "../shared/TagsSection";
import type { FileWithMetadata } from "./MediaUploadModal";

interface UploadPreviewPanelProps {
  /** Files with metadata */
  files: FileWithMetadata[];
  /** Media type being uploaded */
  mediaType: "image" | "video" | "audio";
  /** Callback to remove a file */
  onRemoveFile: (index: number) => void;
  /** Callback to update file metadata */
  onUpdateMetadata: (index: number, updates: Partial<FileWithMetadata>) => void;
}

/**
 * UploadPreviewPanel - Preview panel for upload modal
 *
 * Shows file previews and per-file metadata editing.
 */
export function UploadPreviewPanel({
  files,
  mediaType,
  onRemoveFile,
  onUpdateMetadata,
}: UploadPreviewPanelProps) {
  const showAudioText = mediaType === "audio" || mediaType === "video";
  const [transcribingFiles, setTranscribingFiles] = useState<Record<number, boolean>>({});
  const [transcriptionErrors, setTranscriptionErrors] = useState<Record<number, string | null>>({});

  // Transcription mutation
  const [transcribeFile] = useMutation<
    TranscribeUploadedFileMutation,
    TranscribeUploadedFileMutationVariables
  >(TranscribeUploadedFileDocument);

  const handleTranscribe = async (fileIndex: number) => {
    const fileData = files[fileIndex];
    if (!fileData) return;

    setTranscribingFiles((prev) => ({ ...prev, [fileIndex]: true }));
    setTranscriptionErrors((prev) => ({ ...prev, [fileIndex]: null }));

    try {
      const result = await transcribeFile({
        variables: {
          file: fileData.file,
        },
      });

      const transcriptionResult = result.data?.transcribeUploadedFile;

      if (transcriptionResult?.text) {
        // Update audioText with transcription
        onUpdateMetadata(fileIndex, { audioText: transcriptionResult.text });
        toast.success(
          `Transcription complete!${transcriptionResult.duration ? ` (${transcriptionResult.duration.toFixed(1)}s)` : ""}`
        );
      } else {
        const errorMsg = "Transcription failed";
        setTranscriptionErrors((prev) => ({ ...prev, [fileIndex]: errorMsg }));
        toast.error(errorMsg);
      }
    } catch (error) {
      const outOfCredits = isAiCreditsExhausted(error);
      const errorMsg = outOfCredits
        ? AI_CREDITS_MESSAGE
        : error instanceof Error
          ? error.message
          : "Transcription failed";
      setTranscriptionErrors((prev) => ({ ...prev, [fileIndex]: errorMsg }));
      toast.error(errorMsg, outOfCredits ? { duration: 10000 } : undefined);
    } finally {
      setTranscribingFiles((prev) => ({ ...prev, [fileIndex]: false }));
    }
  };

  return (
    <div className="space-y-4">
      {files.map((fileData, index) => (
        <div
          key={`${fileData.file.name}-${fileData.file.lastModified}-${fileData.file.size}`}
          className="rounded-lg border border-gray-200 bg-white p-4"
        >
          {/* Header: Filename + Remove button */}
          <div className="mb-3 flex items-center justify-between">
            <h3
              className="truncate text-sm font-medium text-secondary-200"
              title={fileData.file.name}
            >
              {fileData.file.name}
            </h3>
            <IconButton
              onClick={() => onRemoveFile(index)}
              variant="ghost"
              aria-label="Remove file"
            >
              <FiX className="h-5 w-5" />
            </IconButton>
          </div>

          {/* Preview */}
          <div className="mb-4">
            {mediaType === "image" && (
              <img
                src={fileData.preview}
                alt={fileData.file.name}
                className="h-48 w-full rounded-md object-contain bg-gray-50"
              />
            )}
            {mediaType === "video" && (
              <video src={fileData.preview} controls className="h-48 w-full rounded-md bg-gray-900">
                <track kind="captions" />
              </video>
            )}
            {mediaType === "audio" && (
              <div className="rounded-md bg-gray-50 p-4">
                <audio src={fileData.preview} controls className="w-full">
                  <track kind="captions" />
                </audio>
              </div>
            )}
          </div>

          {/* Audio Text (for audio/video only) */}
          {showAudioText && (
            <div className="mb-4">
              <label htmlFor={`audioText-${index}`} className="mb-2 block label">
                Description / Transcription
              </label>
              <textarea
                id={`audioText-${index}`}
                value={fileData.audioText}
                onChange={(e) => onUpdateMetadata(index, { audioText: e.target.value })}
                placeholder="Enter description or transcription..."
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />

              {/* Transcription button */}
              <div className="mt-2 space-y-1">
                <Button
                  onClick={() => handleTranscribe(index)}
                  disabled={transcribingFiles[index]}
                  variant="primary"
                  size="sm"
                >
                  {transcribingFiles[index] ? "Transcribing..." : "To text"}
                </Button>
                {transcriptionErrors[index] && (
                  <p className="text-xs text-red-600">{transcriptionErrors[index]}</p>
                )}
              </div>
            </div>
          )}

          {/* Tags */}
          <TagsSection
            value={fileData.tags}
            onChange={(tags) => onUpdateMetadata(index, { tags })}
            required
            helpText="Add tags for this file. Separate tags by commas."
          />
        </div>
      ))}
    </div>
  );
}
